import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { blueGrey, deepPurple } from '@mui/material/colors';
import * as Icons from '../constants/icons';
import LoadingView from './LoadingView';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { useMasterData } from '../providers/MasterDataProvider';
import { useAuth } from '../firebase/AuthProvider';
import { STANDARD_CTA_BUTTON_COMPACT_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import {
  useObcAlertTargets,
  AlertLevel,
  AlertTargetKind,
  ObcAlertTarget,
} from '../hooks/useObcAlertTargets';

/**
 * OBC 同期アラートの通知先カード。
 *
 * 平日 06:00 ルーティン (仕訳差分検知 → フル同期 → 貸借対照表 全量更新) と
 * 月次同期ジョブの結果・異常を Google Chat へ届ける宛先を登録する。
 *   社員     … Hozenall AI エージェントからの 1対1 DM
 *   スペース … Google Chat スペースの受信 Webhook URL
 * 宛先ごとに「どの重要度から届けるか」を選べる。
 */

const LEVEL_OPTIONS: { value: AlertLevel; label: string; hint: string }[] = [
  { value: 'info', label: '毎朝の結果も受け取る', hint: '06:00 の結果サマリーを毎回 (異常なしでも 1 通)' },
  { value: 'warning', label: '差分・エラー以上', hint: '仕訳差分、同期タスクの失敗、貸借不一致など' },
  { value: 'critical', label: '障害のみ', hint: 'API/プロキシ障害、ジョブ自体の失敗、ルーティン停止' },
];

type StaffOption = { staff_id: string; staff_name: string; branch_name?: string; department_name?: string };

const ObcAlertTargetCard: React.FC = () => {
  const notify = useNotification();
  const confirm = useConfirm();
  const { user } = useAuth();
  const { staffList } = useMasterData();
  const { targets, loading, save, remove, sendTest } = useObcAlertTargets();

  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<AlertTargetKind>('dm');
  const [staff, setStaff] = useState<StaffOption | null>(null);
  const [spaceName, setSpaceName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [minLevel, setMinLevel] = useState<AlertLevel>('warning');
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<number | null>(null);

  const operatorEmail = user?.email ?? undefined;

  // 既に DM 登録済みの社員は候補から外す
  const staffOptions = useMemo<StaffOption[]>(() => {
    const registered = new Set(targets.filter((t) => t.target_kind === 'dm').map((t) => (t.user_id || '').toLowerCase()));
    return (staffList as StaffOption[]).filter((s) => s.staff_id && !registered.has(s.staff_id.toLowerCase()));
  }, [staffList, targets]);

  const resetForm = () => {
    setStaff(null);
    setSpaceName('');
    setWebhookUrl('');
    setMinLevel('warning');
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      if (kind === 'dm') {
        if (!staff) {
          notify.warning('社員を選択してください');
          return;
        }
        await save({
          target_kind: 'dm',
          user_id: staff.staff_id,
          display_name: staff.staff_name,
          min_level: minLevel,
          is_active: true,
          operator_email: operatorEmail,
        });
      } else {
        if (!webhookUrl.trim()) {
          notify.warning('受信 Webhook URL を入力してください');
          return;
        }
        await save({
          target_kind: 'webhook',
          display_name: spaceName.trim() || 'Chat スペース',
          webhook_url: webhookUrl.trim(),
          min_level: minLevel,
          is_active: true,
          operator_email: operatorEmail,
        });
      }
      notify.success('通知先を登録しました');
      resetForm();
      setOpen(false);
    } catch (e: any) {
      notify.error(e?.response?.data?.error || e?.message || '登録に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (t: ObcAlertTarget, active: boolean) => {
    try {
      await save({ id: t.id, target_kind: t.target_kind, display_name: t.display_name, min_level: t.min_level, is_active: active, operator_email: operatorEmail });
      notify.success(active ? '通知を有効にしました' : '通知を停止しました');
    } catch (e: any) {
      notify.error(e?.response?.data?.error || e?.message || '更新に失敗しました');
    }
  };

  const handleChangeLevel = async (t: ObcAlertTarget, level: AlertLevel) => {
    try {
      await save({ id: t.id, target_kind: t.target_kind, display_name: t.display_name, min_level: level, is_active: t.is_active, operator_email: operatorEmail });
      notify.success('重要度を変更しました');
    } catch (e: any) {
      notify.error(e?.response?.data?.error || e?.message || '更新に失敗しました');
    }
  };

  const handleDelete = async (t: ObcAlertTarget) => {
    const ok = await confirm({
      title: '通知先の削除',
      message: `${t.display_name} への通知をやめますか？`,
      confirmText: '削除する',
      destructive: true,
    });
    if (!ok) return;
    try {
      await remove(t.id);
      notify.success('通知先を削除しました');
    } catch (e: any) {
      notify.error(e?.response?.data?.error || e?.message || '削除に失敗しました');
    }
  };

  const handleTest = async (t: ObcAlertTarget) => {
    setTestingId(t.id);
    try {
      await sendTest(t.id, operatorEmail);
      notify.success(`${t.display_name} へテスト通知を送りました`);
    } catch (e: any) {
      notify.error(e?.response?.data?.error || e?.message || 'テスト送信に失敗しました');
    } finally {
      setTestingId(null);
    }
  };

  return (
    <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', mt: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icons.Send sx={{ color: deepPurple[400] }} /> アラート通知先
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
          平日 06:00 の自動更新（仕訳差分の検知・貸借対照表の更新）や同期ジョブの結果・異常を Google Chat へ通知します。
        </Typography>

        {loading ? (
          <LoadingView message="通知先を読み込み中..." height="120px" size={28} />
        ) : targets.length === 0 ? (
          <Typography variant="body2" sx={{ color: blueGrey[400], mb: 2 }}>
            通知先はまだ登録されていません。
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {targets.map((t) => (
              <Box
                key={t.id}
                sx={{
                  px: 1.5, py: 1, borderRadius: '10px',
                  bgcolor: t.is_active ? '#f8fafc' : '#f1f5f9',
                  opacity: t.is_active ? 1 : 0.6,
                }}
              >
                {/* 上段: 名前・種別・宛先 (幅いっぱい使う) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                    {t.display_name}
                  </Typography>
                  <Chip size="small" label={t.target_kind === 'dm' ? 'DM' : 'スペース'} sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }} />
                  <Typography
                    variant="caption"
                    sx={{ color: blueGrey[400], minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {t.target_kind === 'dm' ? t.user_id : t.webhook_url}
                  </Typography>
                </Box>
                {/* 下段: 通知範囲・有効切替・テスト・削除 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                  <TextField
                    select
                    size="small"
                    value={t.min_level}
                    onChange={(e) => handleChangeLevel(t, e.target.value as AlertLevel)}
                    sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }}
                  >
                    {LEVEL_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.8rem' }}>{o.label}</MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title={t.is_active ? '通知中（クリックで停止）' : '停止中（クリックで再開）'}>
                    <Switch size="small" checked={t.is_active} onChange={(e) => handleToggleActive(t, e.target.checked)} />
                  </Tooltip>
                  <Tooltip title="テスト通知を送る">
                    <span>
                      <IconButton size="small" disabled={testingId === t.id || !t.is_active} onClick={() => handleTest(t)}>
                        <Icons.Send sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="削除">
                    <IconButton size="small" onClick={() => handleDelete(t)}>
                      <Icons.Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Stack>
        )}

        {!open && (
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Icons.PersonAdd />}
            onClick={() => setOpen(true)}
            sx={{ py: 1.2, borderRadius: '10px', fontWeight: 'bold', borderWidth: '2px', borderStyle: 'solid' }}
          >
            通知先を追加
          </Button>
        )}

        <Collapse in={open} unmountOnExit>
          <Box sx={{ mt: 1, p: 2, borderRadius: '10px', border: '1px solid #e2e8f0' }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              fullWidth
              value={kind}
              onChange={(_, v) => v && setKind(v)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="dm" sx={{ fontSize: '0.8rem' }}>社員（Chat DM）</ToggleButton>
              <ToggleButton value="webhook" sx={{ fontSize: '0.8rem' }}>スペース（Webhook）</ToggleButton>
            </ToggleButtonGroup>

            {kind === 'dm' ? (
              <Autocomplete
                size="small"
                options={staffOptions}
                value={staff}
                onChange={(_, v) => setStaff(v)}
                getOptionLabel={(o) => `${o.staff_name}（${o.staff_id}）`}
                isOptionEqualToValue={(a, b) => a.staff_id === b.staff_id}
                renderInput={(params) => <TextField {...params} label="社員" placeholder="氏名またはメールで検索" />}
                sx={{ mb: 2 }}
              />
            ) : (
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <TextField size="small" label="スペース名（表示用）" value={spaceName} onChange={(e) => setSpaceName(e.target.value)} />
                <TextField
                  size="small"
                  label="受信 Webhook URL"
                  placeholder="https://chat.googleapis.com/v1/spaces/…"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  helperText="Google Chat のスペースで「アプリと統合 → Webhook を管理」から発行します"
                />
              </Stack>
            )}

            <TextField
              select
              size="small"
              fullWidth
              label="通知する範囲"
              value={minLevel}
              onChange={(e) => setMinLevel(e.target.value as AlertLevel)}
              helperText={LEVEL_OPTIONS.find((o) => o.value === minLevel)?.hint}
              sx={{ mb: 2 }}
            >
              {LEVEL_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={() => { resetForm(); setOpen(false); }} sx={STANDARD_TEXT_BUTTON_SX}>
                キャンセル
              </Button>
              <Button onClick={handleAdd} disabled={saving} startIcon={<Icons.Add />} sx={STANDARD_CTA_BUTTON_COMPACT_SX}>
                登録する
              </Button>
            </Box>
          </Box>
        </Collapse>

        {kind === 'dm' && open && (
          <Typography variant="caption" sx={{ color: blueGrey[400], display: 'block', mt: 1 }}>
            DM は「Hozenall AI エージェント」から届きます。届かない場合は、その社員が一度 Chat でエージェントへ話しかけてから「テスト通知」で確認してください。
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default ObcAlertTargetCard;
